import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import readNDJSONStream from "ndjson-readablestream";
import { X } from "lucide-react";

import styles from "./SummaryFlow.module.css";

import { getToken, useMsalAuth } from "../../authConfig";
import { summaryApi, type SummaryRequest } from "../../api/api";
import { AnswerError, AnswerLoading } from "../Answer";
import { InteractiveNoteContent } from "../InteractiveNoteContent";
import { QuestionInput } from "../QuestionInput";
import { UserChatMessage } from "../UserChatMessage";

interface SummaryFlowProps {
    onDismiss: () => void;
}

type MessageTuple = [user: string, response: string];

export const SummaryFlow = ({ onDismiss }: SummaryFlowProps) => {
    const { t, i18n } = useTranslation();
    const client = useMsalAuth ? useMsal().instance : undefined;

    const [conversationHistory, setConversationHistory] = useState<MessageTuple[]>([]);
    const [initialStreamedContent, setInitialStreamedContent] = useState("");
    const [pendingQuestion, setPendingQuestion] = useState("");
    const [streamedContent, setStreamedContent] = useState("");
    const [sessionState, setSessionState] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [requestKey, setRequestKey] = useState(0);
    const [lastFailedQuestion, setLastFailedQuestion] = useState<string | null>(null);

    const resolveAuthToken = useCallback(async (): Promise<string | undefined> => {
        try {
            if (client) {
                return await getToken(client);
            }
            return "";
        } catch (authError) {
            console.error("Failed to resolve auth token:", authError);
            return "";
        }
    }, [client]);

    useEffect(() => {
        let cancelled = false;

        const fetchSummary = async () => {
            setConversationHistory([]);
            setInitialStreamedContent("");
            setPendingQuestion("");
            setStreamedContent("");
            setSessionState(null);
            setError(null);
            setLastFailedQuestion(null);
            setIsLoading(true);
            setIsStreaming(false);

            try {
                const token = await resolveAuthToken();
                const response = await summaryApi(
                    {
                        context: {
                            overrides: {
                                language: i18n.language
                            }
                        }
                    },
                    token
                );

                if (!response.body) {
                    throw new Error("No response body");
                }

                let fullResponse = "";
                for await (const event of readNDJSONStream(response.body)) {
                    if (cancelled) {
                        return;
                    }

                    if (event.error) {
                        throw new Error(event.error);
                    }

                    if (event.session_state) {
                        setSessionState(event.session_state);
                    }

                    if (event.delta?.content) {
                        fullResponse += event.delta.content;
                        setInitialStreamedContent(fullResponse);
                    }
                }

                if (!cancelled) {
                    setConversationHistory([[t("summaryFlow.userLabel"), fullResponse]]);
                    setInitialStreamedContent("");
                }
            } catch (requestError) {
                if (!cancelled) {
                    setError(
                        requestError instanceof Error
                            ? requestError.message
                            : t("summaryFlow.error")
                    );
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        void fetchSummary();

        return () => {
            cancelled = true;
        };
    }, [i18n.language, requestKey, resolveAuthToken, t]);

    const makeFollowUpRequest = useCallback(
        async (question: string) => {
            if (!question.trim() || isLoading || isStreaming || conversationHistory.length === 0) {
                return;
            }

            setPendingQuestion(question);
            setStreamedContent("");
            setError(null);
            setLastFailedQuestion(question);
            setIsStreaming(true);

            try {
                const token = await resolveAuthToken();
                const messages = conversationHistory.flatMap(([user, response]) => [
                    { role: "user", content: user },
                    { role: "assistant", content: response }
                ]);

                const request: SummaryRequest = {
                    messages: [...messages, { role: "user", content: question }],
                    context: {
                        overrides: {
                            language: i18n.language
                        }
                    },
                    session_state: sessionState || undefined
                };

                const response = await summaryApi(request, token);

                if (!response.body) {
                    throw new Error("No response body");
                }

                let fullResponse = "";
                for await (const event of readNDJSONStream(response.body)) {
                    if (event.error) {
                        throw new Error(event.error);
                    }

                    if (event.session_state) {
                        setSessionState(event.session_state);
                    }

                    if (event.delta?.content) {
                        fullResponse += event.delta.content;
                        setStreamedContent(fullResponse);
                    }
                }

                setConversationHistory(previousHistory => [...previousHistory, [question, fullResponse]]);
                setPendingQuestion("");
                setStreamedContent("");
                setLastFailedQuestion(null);
            } catch (requestError) {
                setError(
                    requestError instanceof Error
                        ? requestError.message
                        : t("summaryFlow.error")
                );
            } finally {
                setIsStreaming(false);
            }
        },
        [conversationHistory, i18n.language, isLoading, isStreaming, resolveAuthToken, sessionState, t]
    );

    const handleRetry = useCallback(() => {
        if (conversationHistory.length === 0) {
            setRequestKey(previous => previous + 1);
            return;
        }

        if (lastFailedQuestion) {
            void makeFollowUpRequest(lastFailedQuestion);
        }
    }, [conversationHistory.length, lastFailedQuestion, makeFollowUpRequest]);

    const isInputDisabled = isLoading || isStreaming || conversationHistory.length === 0;

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <button type="button" className={styles.dismissButton} onClick={onDismiss}>
                    <X size={18} />
                    {t("summaryFlow.dismiss")}
                </button>

                <UserChatMessage message={t("summaryFlow.userLabel")} />

                <div className={styles.aiMessage}>
                    {t("summaryFlow.subtitle")}
                </div>

                {isLoading && !initialStreamedContent && conversationHistory.length === 0 && (
                    <div className={styles.noteContentWrapper}>
                        <AnswerLoading />
                    </div>
                )}

                {initialStreamedContent && conversationHistory.length === 0 && (
                    <div className={styles.noteContentWrapper}>
                        <InteractiveNoteContent content={initialStreamedContent} onContentChange={() => undefined} isEditable={false} />
                    </div>
                )}

                {conversationHistory.map(([userMessage, response], index) => (
                    <div key={`${userMessage}-${index}`}>
                        {index > 0 && <UserChatMessage message={userMessage} />}
                        <div className={styles.noteContentWrapper}>
                            <InteractiveNoteContent content={response} onContentChange={() => undefined} isEditable={false} />
                        </div>
                    </div>
                ))}

                {isStreaming && pendingQuestion && (
                    <UserChatMessage message={pendingQuestion} />
                )}

                {isStreaming && (
                    <div className={styles.noteContentWrapper}>
                        {streamedContent ? (
                            <InteractiveNoteContent content={streamedContent} onContentChange={() => undefined} isEditable={false} />
                        ) : (
                            <AnswerLoading />
                        )}
                    </div>
                )}

                {error && pendingQuestion && conversationHistory[conversationHistory.length - 1]?.[0] !== pendingQuestion && (
                    <UserChatMessage message={pendingQuestion} />
                )}

                {error && (
                    <div className={styles.noteContentWrapper}>
                        <AnswerError error={error} onRetry={handleRetry} />
                    </div>
                )}
            </div>

            <div className={styles.chatInput}>
                <QuestionInput
                    clearOnSend
                    placeholder={t("summaryFlow.followUpPlaceholder")}
                    disabled={isInputDisabled}
                    onSend={(question, _isWebSearch) => void makeFollowUpRequest(question)}
                    showSpeechInput
                />
            </div>
        </div>
    );
};
